import React, { useState, useEffect } from 'react';
import apiClient from '../services/apiClient';
import StakeTable from './GameEngine/StakeTable';
import { Button, Modal, FormInput, FormMultiSelect } from '../components/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

// ─── Country list ──────────────────────────────────────────────────────────────
const COUNTRIES = [
  "Afghanistan","Albania","Algeria","Andorra","Angola","Argentina","Armenia","Australia","Austria","Azerbaijan",
  "Bahamas","Bahrain","Bangladesh","Belarus","Belgium","Belize","Benin","Bhutan","Bolivia","Bosnia","Botswana",
  "Brazil","Brunei","Bulgaria","Burkina Faso","Burundi","Cambodia","Cameroon","Canada","Chad","Chile","China",
  "Colombia","Congo","Costa Rica","Croatia","Cuba","Cyprus","Czech Republic","Denmark","Dominican Republic",
  "Ecuador","Egypt","El Salvador","Estonia","Ethiopia","Fiji","Finland","France","Gabon","Georgia","Germany",
  "Ghana","Greece","Guatemala","Guinea","Haiti","Honduras","Hungary","Iceland","India","Indonesia","Iran",
  "Iraq","Ireland","Israel","Italy","Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kuwait","Kyrgyzstan",
  "Laos","Latvia","Lebanon","Libya","Lithuania","Luxembourg","Madagascar","Malaysia","Maldives","Mali","Malta",
  "Mauritania","Mauritius","Mexico","Moldova","Mongolia","Montenegro","Morocco","Mozambique","Myanmar","Namibia",
  "Nepal","Netherlands","New Zealand","Nicaragua","Niger","Nigeria","Norway","Oman","Pakistan","Palestine",
  "Panama","Papua New Guinea","Paraguay","Peru","Philippines","Poland","Portugal","Qatar","Romania","Russia",
  "Rwanda","Saudi Arabia","Senegal","Serbia","Sierra Leone","Singapore","Slovakia","Slovenia","Somalia",
  "South Africa","South Korea","Spain","Sri Lanka","Sudan","Sweden","Switzerland","Syria","Taiwan","Tajikistan",
  "Tanzania","Thailand","Togo","Trinidad and Tobago","Tunisia","Turkey","Turkmenistan","Uganda","Ukraine",
  "United Arab Emirates","United Kingdom","United States","Uruguay","Uzbekistan","Venezuela","Vietnam","Yemen",
  "Zambia","Zimbabwe"
];

const COUNTRY_OPTIONS = COUNTRIES.map(c => ({ value: c, label: c }));

// ─── Country Badges helper ─────────────────────────────────────────────────────
const CountriesBadges: React.FC<{ countries: string[] }> = ({ countries }) => {
  if (!countries || countries.length === 0) {
    return <span className="text-slate-500 text-xs italic">All Countries</span>;
  }
  const display = countries.slice(0, 2);
  const extra = countries.length - 2;
  return (
    <div className="flex flex-wrap gap-1 items-center max-w-[200px]">
      {display.map(c => (
        <span key={c} className="px-1.5 py-0.5 bg-indigo-900/50 text-indigo-300 border border-indigo-700/40 rounded text-[10px] font-semibold whitespace-nowrap">{c}</span>
      ))}
      {extra > 0 && (
        <span className="px-1.5 py-0.5 bg-slate-700/50 text-slate-300 border border-slate-600/40 rounded text-[10px] font-semibold">+{extra} more</span>
      )}
    </div>
  );
};

// ─── Interfaces ────────────────────────────────────────────────────────────────
interface PayoutRule {
  id: number;
  manager_id: number;
  manager_name: string;
  affiliate_id: number;
  affiliate_name: string;
  min_deposit_usd: string;
  min_wager_usd: string;
  commission_per_user: string;
  start_date: string;
  end_date: string;
  created_at: string;
  target_countries: string[];
}

interface DropdownItem {
  id: number;
  username: string;
  email: string;
}

interface RulesResponse {
  data: PayoutRule[];
  total: number;
}

// ─── API helpers ───────────────────────────────────────────────────────────────
const fetchRulesAPI = async (filters: { page: number; limit: number; search: string; startDate: string; endDate: string }): Promise<RulesResponse> => {
  const token = localStorage.getItem('token');
  const params = new URLSearchParams({
    page: String(filters.page),
    limit: String(filters.limit),
    search: filters.search,
    startDate: filters.startDate,
    endDate: filters.endDate
  });
  const res = await apiClient.get(`/admin/affiliate-payouts/rules?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data.success ? { data: res.data.data, total: res.data.total } : { data: [], total: 0 };
};

const fetchManagersAPI = async (): Promise<DropdownItem[]> => {
  const token = localStorage.getItem('token');
  const res = await apiClient.get('/admin/affiliate-payouts/managers', {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data.success ? res.data.data : [];
};

const fetchAffiliatesAPI = async (managerId?: string): Promise<DropdownItem[]> => {
  const token = localStorage.getItem('token');
  const url = managerId ? `/admin/affiliate-payouts/affiliates?managerId=${managerId}` : '/admin/affiliate-payouts/affiliates';
  const res = await apiClient.get(url, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data.success ? res.data.data : [];
};

// ─── Main Component ────────────────────────────────────────────────────────────
const AffiliatePayoutRules: React.FC = () => {
  const queryClient = useQueryClient();
  const role = (localStorage.getItem('role') || '').toLowerCase().trim();
  const isAdmin = role === 'admin';

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [limit, setLimit] = useState(25);
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  const { data = { data: [], total: 0 }, isLoading } = useQuery({
    queryKey: ['payoutRules', page, limit, searchTerm, filterStartDate, filterEndDate],
    queryFn: () => fetchRulesAPI({ page, limit, search: searchTerm, startDate: filterStartDate, endDate: filterEndDate })
  });
  const { data: managers = [] } = useQuery({ queryKey: ['payoutManagers'], queryFn: fetchManagersAPI, enabled: isAdmin });

  const rules = data.data;
  const total = data.total;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<PayoutRule | null>(null);
  const [selectedManagers, setSelectedManagers] = useState<string[]>([]);
  const [selectedAffiliates, setSelectedAffiliates] = useState<string[]>([]);
  const [affiliates, setAffiliates] = useState<DropdownItem[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);

  const [formData, setFormData] = useState({
    minDepositUsd: '0',
    minWagerUsd: '0',
    commissionPerUser: '0',
    startDate: '',
    endDate: ''
  });

  // Fetch affiliates whenever manager selection changes
  useEffect(() => {
    if (selectedManagers.length > 0) {
      const idsParam = selectedManagers.join(',');
      fetchAffiliatesAPI(idsParam).then(data => {
        setAffiliates(data);
        const validIds = data.map((a: DropdownItem) => String(a.id));
        setSelectedAffiliates(prev => prev.filter(id => validIds.includes(id)));
      });
    } else {
      setAffiliates([]);
      setSelectedAffiliates([]);
    }
  }, [selectedManagers]);

  // ─── Mutations ───────────────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: async (data: any) => {
      const token = localStorage.getItem('token');
      return apiClient.post('/admin/affiliate-payouts/rules', data, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payoutRules'] });
      toast.success("Payout rule created successfully!");
      closeModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to save rule");
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const token = localStorage.getItem('token');
      return apiClient.delete(`/admin/affiliate-payouts/rules/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payoutRules'] });
      toast.success("Rule deleted successfully!");
    },
    onError: () => {
      toast.error("Failed to delete rule");
    }
  });

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      const token = localStorage.getItem('token');
      return apiClient.put(`/admin/affiliate-payouts/rules/${data.id}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payoutRules'] });
      toast.success("Payout rule updated successfully!");
      closeModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to update rule");
    }
  });

  const currentUid = localStorage.getItem('uid') || '';

  // ─── Modal helpers ────────────────────────────────────────────────────────────
  const openModal = () => {
    setEditingRule(null);
    setSelectedManagers(role === 'manager' ? [currentUid] : []);
    setSelectedAffiliates([]);
    setSelectedCountries([]);
    setFormData({ minDepositUsd: '0', minWagerUsd: '0', commissionPerUser: '0', startDate: '', endDate: '' });
    setIsModalOpen(true);
  };

  const openEditModal = (rule: PayoutRule) => {
    setEditingRule(rule);
    setSelectedManagers([String(rule.manager_id)]);
    setSelectedAffiliates([String(rule.affiliate_id)]);
    setSelectedCountries(Array.isArray(rule.target_countries) ? rule.target_countries : []);
    setFormData({
      minDepositUsd: String(rule.min_deposit_usd),
      minWagerUsd: String(rule.min_wager_usd),
      commissionPerUser: String(rule.commission_per_user),
      startDate: rule.start_date.substring(0, 10),
      endDate: rule.end_date.substring(0, 10)
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingRule(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingRule) {
      updateMutation.mutate({
        id: editingRule.id,
        managerId: selectedManagers[0],
        affiliateId: selectedAffiliates[0],
        targetCountries: selectedCountries,
        ...formData
      });
    } else {
      if (selectedManagers.length === 0) { toast.error("Please select at least one Manager"); return; }
      if (selectedAffiliates.length === 0) { toast.error("Please select at least one Affiliate"); return; }
      saveMutation.mutate({
        ...formData,
        managerIds: selectedManagers,
        affiliateIds: selectedAffiliates,
        targetCountries: selectedCountries
      });
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm("Are you sure you want to delete this rule?")) {
      deleteMutation.mutate(id);
    }
  };

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        search: searchTerm,
        startDate: filterStartDate,
        endDate: filterEndDate,
        export: 'true'
      });
      const res = await apiClient.get(`/admin/affiliate-payouts/rules?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.data.success || !res.data.data || res.data.data.length === 0) {
        toast.info("No data to export");
        return;
      }
      const dataToExport = res.data.data;
      const headers = ["ID", "Manager", "Affiliate", "Min Deposit (USD)", "Min Wager (USD)", "Commission (USD)", "Start Date", "End Date", "Target Countries"];
      const csvRows = [headers.join(",")];
      for (const item of dataToExport) {
        const row = [
          item.id,
          `"${item.manager_name.replace(/"/g, '""')}"`,
          `"${item.affiliate_name.replace(/"/g, '""')}"`,
          parseFloat(item.min_deposit_usd || '0').toFixed(2),
          parseFloat(item.min_wager_usd || '0').toFixed(2),
          parseFloat(item.commission_per_user || '0').toFixed(2),
          new Date(item.start_date).toLocaleDateString(),
          new Date(item.end_date).toLocaleDateString(),
          `"${(item.target_countries || []).join('; ')}"`
        ];
        csvRows.push(row.join(","));
      }
      const blob = new Blob([csvRows.join("\n")], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `affiliate_payout_rules_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
      toast.error("Failed to export data");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  // ─── Table columns ────────────────────────────────────────────────────────────
  const columns = [
    { key: 'id', label: 'ID', width: 'w-16' },
    { key: 'manager_name', label: 'Manager' },
    { key: 'affiliate_name', label: 'Affiliate' },
    { key: 'min_deposit_usd', label: 'Min Deposit ($)', render: (val: string) => `$${parseFloat(val).toFixed(2)}` },
    { key: 'min_wager_usd', label: 'Min Wager ($)', render: (val: string) => `$${parseFloat(val).toFixed(2)}` },
    { key: 'commission_per_user', label: 'Commission ($)', render: (val: string) => `$${parseFloat(val).toFixed(2)}` },
    {
      key: 'target_countries',
      label: 'Target Countries',
      render: (val: string[]) => <CountriesBadges countries={val} />
    },
    { key: 'start_date', label: 'Start Date', render: (val: string) => new Date(val).toLocaleDateString() },
    { key: 'end_date', label: 'End Date', render: (val: string) => new Date(val).toLocaleDateString() },
    ...((isAdmin || role === 'manager') ? [{
      key: 'actions', label: 'Actions', textAlign: 'right' as const,
      render: (_: any, item: PayoutRule) => (
        <div className="flex justify-end gap-2">
          <Button variant="primary" size="xs" onClick={() => openEditModal(item)}>Edit</Button>
          <Button variant="danger" size="xs" onClick={() => handleDelete(item.id)}>Delete</Button>
        </div>
      )
    }] : [])
  ];

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="ml-0 lg:ml-[256px] p-4 sm:p-6 min-h-screen text-white bg-[#151929]">
      <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-4 bg-slate-900 border border-slate-800 p-4 rounded-xl mb-4">
        <div className="w-full sm:w-48">
          <FormInput
            type="date"
            label="Filter Start Date"
            value={filterStartDate}
            onChange={e => { setFilterStartDate(e.target.value); setPage(1); }}
            className="[color-scheme:dark]"
          />
        </div>
        <div className="w-full sm:w-48">
          <FormInput
            type="date"
            label="Filter End Date"
            value={filterEndDate}
            onChange={e => { setFilterEndDate(e.target.value); setPage(1); }}
            className="[color-scheme:dark]"
          />
        </div>
        {(filterStartDate || filterEndDate) && (
          <Button variant="ghost" size="sm" onClick={() => { setFilterStartDate(''); setFilterEndDate(''); setPage(1); }}>
            Clear Dates
          </Button>
        )}
      </div>

      <StakeTable
        title="Affiliate Payout Rules"
        columns={columns}
        data={rules}
        isLoading={isLoading}
        searchPlaceholder="Search by Affiliate or Manager..."
        onSearch={(q) => { setSearchTerm(q); setPage(1); }}
        page={page}
        totalPages={totalPages}
        total={total}
        limit={limit}
        onLimitChange={setLimit}
        onPageChange={setPage}
        selectable={false}
        emptyMessage="No rules configured."
        actions={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={handleExportCSV}>Export CSV</Button>
            {(isAdmin || role === 'manager') && <Button variant="success" onClick={openModal}>+ Add Payout Rule</Button>}
          </div>
        }
        hasSidebarMargin={false}
      />

      <Modal
        open={isModalOpen}
        onClose={closeModal}
        title={editingRule ? "Edit Payout Rule" : "Configure Payout Rule"}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={closeModal}>Cancel</Button>
            <Button
              variant="primary"
              size="sm"
              loading={saveMutation.isPending || updateMutation.isPending}
              onClick={handleSubmit}
            >
              {editingRule ? "Save Changes" : "Create Rule"}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {editingRule ? (
            <div className="bg-[#151929] border border-slate-700/60 rounded-md p-3 space-y-2 text-sm text-slate-300">
              <div><span className="font-semibold text-slate-400">Manager:</span> {editingRule.manager_name}</div>
              <div><span className="font-semibold text-slate-400">Affiliate:</span> {editingRule.affiliate_name}</div>
            </div>
          ) : (
            <>
              {isAdmin && (
                <FormMultiSelect
                  label="Select Managers"
                  placeholder="Search and Select Managers"
                  options={managers.map((m: DropdownItem) => ({ value: String(m.id), label: m.username }))}
                  selectedValues={selectedManagers}
                  onChange={setSelectedManagers}
                />
              )}
              <FormMultiSelect
                label="Select Affiliates"
                placeholder={selectedManagers.length === 0 ? "Select Manager first" : "Search and Select Affiliates"}
                options={affiliates.map((a: DropdownItem) => ({ value: String(a.id), label: a.username }))}
                selectedValues={selectedAffiliates}
                onChange={setSelectedAffiliates}
              />
            </>
          )}

          {/* Target Countries */}
          <div>
            <FormMultiSelect
              label="Target Countries (optional)"
              placeholder="Leave empty for all countries, or select specific ones"
              options={COUNTRY_OPTIONS}
              selectedValues={selectedCountries}
              onChange={setSelectedCountries}
            />
            {selectedCountries.length === 0 ? (
              <p className="text-xs text-slate-500 mt-1">Leave empty to apply this rule to users from all countries</p>
            ) : (
              <p className="text-xs text-indigo-400 mt-1">{selectedCountries.length} countr{selectedCountries.length === 1 ? 'y' : 'ies'} selected</p>
            )}
          </div>

          <FormInput
            type="number"
            label="Min Deposit Threshold (USD)"
            required
            value={formData.minDepositUsd}
            onChange={e => setFormData({ ...formData, minDepositUsd: e.target.value })}
          />
          <FormInput
            type="number"
            label="Min Wager Threshold (USD)"
            required
            value={formData.minWagerUsd}
            onChange={e => setFormData({ ...formData, minWagerUsd: e.target.value })}
          />
          <FormInput
            type="number"
            label="Commission Earned Per User (USD)"
            required
            value={formData.commissionPerUser}
            onChange={e => setFormData({ ...formData, commissionPerUser: e.target.value })}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormInput
              type="date"
              label="Start Date"
              required
              value={formData.startDate}
              onChange={e => setFormData({ ...formData, startDate: e.target.value })}
            />
            <FormInput
              type="date"
              label="End Date"
              required
              value={formData.endDate}
              onChange={e => setFormData({ ...formData, endDate: e.target.value })}
            />
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default AffiliatePayoutRules;

